import React from 'react'
import { Tag, theme } from 'antd'
import type { GlobalToken } from 'antd'
import { radius } from '@vacationtracker/shared/constants/ThemeSetting'

export type StatusBadgeTone = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'

export interface StatusBadgeProps {
  /** Fixed semantic palette, pulled from the live antd theme tokens. Default: 'neutral'. */
  tone?: StatusBadgeTone
  /** Escape hatch for data-driven per-entity colors (e.g. a leave type's or label's own color). Overrides `tone`. */
  color?: string
  /** 'pill' is the fully rounded, chip-like look (the UserLabels pattern). */
  shape?: 'default' | 'pill'
  icon?: React.ReactNode
  closable?: boolean
  onClose?: () => void
  children: React.ReactNode
  /** Escape hatch for callers with existing CSS keyed off a class, same pattern as Text/TextField. */
  className?: string
}

/** Text on a tint is its own token (colorSuccessText and friends), not the semantic colour: antd's
 * #52C41A on its own #F6FFED measures 2.21:1. The semantic colours still carry borders and every
 * filled control - only the reading text moves. */
function getToneStyle(tone: StatusBadgeTone, token: GlobalToken): React.CSSProperties {
  switch (tone) {
    case 'brand':
      return { color: token.colorPrimary, background: token.colorPrimaryBg, borderColor: token.colorPrimaryBorder }
    case 'success':
      return { color: token.colorSuccessText, background: token.colorSuccessBg, borderColor: token.colorSuccessBorder }
    case 'warning':
      return { color: token.colorWarningText, background: token.colorWarningBg, borderColor: token.colorWarningBorder }
    case 'danger':
      return { color: token.colorErrorText, background: token.colorErrorBg, borderColor: token.colorErrorBorder }
    case 'info':
      return { color: token.colorInfo, background: token.colorInfoBg, borderColor: token.colorInfoBorder }
    case 'neutral':
    default:
      return { color: token.colorTextSecondary, background: token.colorFillTertiary, borderColor: token.colorBorder }
  }
}

/**
 * Vacation Tracker's single status/tag pill. Replaces 81 raw antd `<Tag>` usages across
 * 41 files plus 6 independent hand-rolled status-pill implementations found in the
 * component audit (see docs/component-audit/tags-badges.html).
 *
 * Semantic colors come from the live antd theme tokens (light/dark aware) rather than
 * invented hex values. The `color` prop is an escape hatch for per-entity
 * colors (leave types, labels) that must stay admin-customizable and isn't meant to be
 * folded into `tone`.
 */
export function StatusBadge({ tone = 'neutral', color, shape = 'default', icon, closable, onClose, children, className }: StatusBadgeProps) {
  const { token } = theme.useToken()

  const style: React.CSSProperties = color ? { color, background: `${color}1a`, borderColor: color } : getToneStyle(tone, token)

  if (shape === 'pill') {
    style.borderRadius = radius.full
    style.padding = '0 12px'
  }

  return (
    <Tag className={className} style={style} icon={icon} closable={closable} onClose={onClose}>
      {children}
    </Tag>
  )
}

export default StatusBadge
